import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useLocation, useRoute } from "wouter";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Lock,
  ClipboardList,
  Download,
  ImagePlus,
  Loader2,
  Send,
} from "lucide-react";
import { ApiError, apiMutation, apiRequest } from "@/api/client";
import FileDropzone from "@/components/FileDropzone";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

type EnaSubmissionSummary = {
  id: number;
  quoteId: number;
  mode: string;
  status: string;
  payloadHash: string;
  payloadVersion: string;
  cutoutFileUrl: string | null;
  enaApplicationId: string | null;
  enaStatusLabel: string | null;
};

type ApplicationPayload = {
  payloadVersion?: string;
  address?: string | null;
  postcode?: string | null;
  customerName?: string | null;
  systemCapacityKw?: string | number | null;
  devicesToInstall?: Array<{ deviceSysRef?: string }>;
  cutOutImage?: { file_url?: string };
};

type PreviewResponse = {
  submission: EnaSubmissionSummary;
  payload: ApplicationPayload;
  payloadHash: string;
  attestationTextVersion: string;
};

type SubmitResponse = {
  mode: "option_a" | "option_b";
  export?: ApplicationPayload;
  submission: EnaSubmissionSummary;
};

function displayValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "Not set";
  return String(value);
}

function systemSizeLooksMissing(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined || value === "") return true;
  const numeric = Number(value);
  return !Number.isFinite(numeric) || numeric <= 0;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export default function InstallerEnaDnoReview() {
  const [, params] = useRoute("/installer/dno/:quoteId/review");
  const quoteId = params?.quoteId ?? "";
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [attested, setAttested] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const previewQuery = useQuery({
    queryKey: ["ena-submission-preview", quoteId],
    queryFn: () => apiRequest<PreviewResponse>(`/api/ena/submissions/${quoteId}/preview`),
    enabled: quoteId !== "",
  });

  const uploadMutation = useMutation<EnaSubmissionSummary, ApiError, File>({
    mutationFn: async (selected: File) => {
      const form = new FormData();
      form.append("file", selected);
      return apiMutation<EnaSubmissionSummary>(
        `/api/ena/submissions/${quoteId}/cutout`,
        form,
      );
    },
    onSuccess: () => {
      setUploadError("");
      toast.success("Cut-out photo saved.");
      queryClient.invalidateQueries({ queryKey: ["ena-submission-preview", quoteId] });
    },
    onError: (error) => {
      const message = errorMessage(error, "Could not upload the photo.");
      setUploadError(message);
      toast.error(message);
    },
  });

  const confirmMutation = useMutation<EnaSubmissionSummary, ApiError, void>({
    mutationFn: () =>
      apiMutation<EnaSubmissionSummary>(`/api/ena/submissions/${quoteId}/confirm`, {
        payload_hash: previewQuery.data?.payloadHash,
        attestation_accepted: true,
      }),
    onSuccess: (saved: EnaSubmissionSummary) => {
      queryClient.setQueryData<PreviewResponse>(["ena-submission-preview", quoteId], (current) =>
        current ? { ...current, submission: saved } : current,
      );
      toast.success("Details locked. You can send the application now.");
    },
    onError: (error) => toast.error(errorMessage(error, "Could not lock these details.")),
  });

  const submitMutation = useMutation({
    mutationFn: () => apiMutation<SubmitResponse>(`/api/ena/submissions/${quoteId}/submit`, {}),
    onSuccess: (data) => {
      if (data.mode === "option_b") {
        toast.success("Application file is ready to download.");
      } else {
        toast.success("Application sent to the network.");
      }
      queryClient.invalidateQueries({ queryKey: ["ena-submission-preview", quoteId] });
    },
    onError: (error) => toast.error(errorMessage(error, "Could not send this application.")),
  });

  const payload = previewQuery.data?.payload;
  const submission = previewQuery.data?.submission;
  const photoSaved = Boolean(submission?.cutoutFileUrl);
  const locked = submission?.status === "confirmed";
  const sent = submission?.status === "submitted";
  const handedOff = submission?.status === "handed_off";
  const finished = sent || handedOff || Boolean(submitMutation.data);
  const sizeMissing = systemSizeLooksMissing(payload?.systemCapacityKw);
  const devices = payload?.devicesToInstall ?? [];
  const placeholderDevice = devices.some((device) =>
    (device.deviceSysRef ?? "").includes("PLACEHOLDER"),
  );

  const nextStep = useMemo(() => {
    if (finished) return 4;
    if (locked) return 4;
    if (!photoSaved) return 2;
    return 3;
  }, [finished, locked, photoSaved]);

  const downloadExport = () => {
    const exportPayload = submitMutation.data?.export ?? payload;
    if (!exportPayload) return;
    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `dno-application-${quoteId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#0B1F3B] text-white">
      <header className="border-b border-white/8 bg-[#0a1628]">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-4">
          <button
            type="button"
            onClick={() => navigate("/installer")}
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to jobs
          </button>
          <img src="/powlo-logo-lime.svg" alt="Powlo" className="h-6 w-auto" />
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-6 px-6 py-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-lime">DNO application</p>
          <h1 className="font-display mt-1 text-3xl font-semibold">Check this before you send</h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/60">
            This is the connection application Powlo will send for this job. Read each section,
            add a photo of the cut-out, then confirm and send. You can go back at any time before you send.
          </p>
        </div>

        <ol className="grid gap-2 sm:grid-cols-4">
          {[
            { n: 1, label: "Check details" },
            { n: 2, label: "Add photo" },
            { n: 3, label: "Confirm" },
            { n: 4, label: "Send" },
          ].map((step) => {
            const done = nextStep > step.n || finished;
            const current = nextStep === step.n && !finished;
            return (
              <li
                key={step.n}
                className={`rounded-lg border px-3 py-2 text-sm ${
                  current
                    ? "border-brand-lime bg-brand-lime/10 text-white"
                    : done
                      ? "border-white/10 bg-white/5 text-white/70"
                      : "border-white/8 text-white/40"
                }`}
              >
                <span className="mr-2 font-semibold">{done ? "✓" : step.n}</span>
                {step.label}
              </li>
            );
          })}
        </ol>

        {previewQuery.isLoading && (
          <div className="flex items-center gap-2 text-white/60">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading this job…
          </div>
        )}

        {previewQuery.isError && (
          <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-200">
            We could not load this application. Go back to jobs and try again.
          </div>
        )}

        {payload && (
          <section className="rounded-xl border border-white/8 bg-[#0d1b2e] p-6">
            <div className="mb-4 flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-brand-lime" />
              <h2 className="font-display text-lg font-semibold">What will be sent</h2>
            </div>
            <dl className="grid gap-4 sm:grid-cols-2">
              <Detail label="Customer" value={displayValue(payload.customerName)} />
              <Detail label="Postcode" value={displayValue(payload.postcode)} />
              <Detail label="Address" value={displayValue(payload.address)} className="sm:col-span-2" />
              <Detail
                label="System size"
                value={sizeMissing ? "Not set" : `${payload.systemCapacityKw} kW`}
                warning={sizeMissing}
              />
              <Detail
                label="Cut-out photo"
                value={photoSaved ? "Added" : "Still needed"}
                warning={!photoSaved}
              />
            </dl>

            <div className="mt-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-white/40">Equipment IDs</h3>
              <p className="mt-1 text-sm text-white/55">
                These are the official network IDs for the kit on this job. They should match what you will install.
              </p>
              <ul className="mt-3 space-y-2">
                {devices.length === 0 && (
                  <li className="rounded-lg bg-white/5 px-3 py-2 text-sm text-white/50">No equipment listed.</li>
                )}
                {devices.map((device, index) => (
                  <li key={`${device.deviceSysRef}-${index}`} className="rounded-lg bg-white/5 px-3 py-2 text-sm">
                    {device.deviceSysRef || "Missing ID"}
                  </li>
                ))}
              </ul>
            </div>

            {(sizeMissing || placeholderDevice) && (
              <div className="mt-5 flex gap-3 rounded-lg border border-amber-400/30 bg-amber-500/10 p-4 text-sm text-amber-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  {sizeMissing && "System size is missing or zero. "}
                  {placeholderDevice && "Equipment IDs look incomplete. "}
                  Check the quote before you send this application.
                </p>
              </div>
            )}
          </section>
        )}

        {!finished && payload && (
          <section className="rounded-xl border border-white/8 bg-[#0d1b2e] p-6">
            <div className="mb-1 flex items-center gap-2">
              <ImagePlus className="h-5 w-5 text-brand-lime" />
              <h2 className="font-display text-lg font-semibold">2. Photo of the cut-out</h2>
            </div>
            <p className="text-sm text-white/55">
              Take a clear photo of the property’s main fuse (cut-out). PNG only, up to 5 MB.
            </p>

            <div className="mt-4">
              <FileDropzone
                accept="image/png,.png"
                title="Drop the cut-out photo here"
                hint="PNG only, up to 5 MB · drag and drop or click to browse"
                fileName={photoSaved ? "Cut-out photo saved" : ""}
                loading={uploadMutation.isPending}
                loadingText="Saving photo…"
                disabled={locked}
                error={uploadError}
                onFileSelected={(selected) => {
                  const isPng =
                    selected.type === "image/png" || selected.name.toLowerCase().endsWith(".png");
                  if (!isPng) {
                    setUploadError("Please use a PNG photo of the cut-out.");
                    return;
                  }
                  if (selected.size > 5 * 1024 * 1024) {
                    setUploadError("That photo is larger than 5 MB. Choose a smaller PNG.");
                    return;
                  }
                  setUploadError("");
                  uploadMutation.mutate(selected);
                }}
              />
            </div>
          </section>
        )}

        {!finished && payload && (
          <section className="rounded-xl border border-white/8 bg-[#0d1b2e] p-6">
            <h2 className="font-display text-lg font-semibold">3. Confirm the details are right</h2>
            <p className="mt-1 text-sm text-white/55">
              {locked
                ? "These details are locked. The next step is to send the application."
                : "Tick the box, then lock the details. After that, the only next step is to send."}
            </p>
            <div className="mt-4 flex items-start gap-3">
              <Checkbox
                id="attest"
                checked={attested || locked}
                disabled={!photoSaved || locked}
                onCheckedChange={(value) => setAttested(value === true)}
                className="mt-0.5 border-white/30"
              />
              <label htmlFor="attest" className="text-sm leading-relaxed text-white/80">
                I have checked the customer, address, system size and equipment. They are correct,
                and I authorise Powlo to send this application for me.
              </label>
            </div>
            {!photoSaved && (
              <p className="mt-3 text-sm text-white/45">Add the cut-out photo before you confirm.</p>
            )}
            {locked ? (
              <div
                className="mt-4 flex items-start gap-3 rounded-lg border border-brand-lime/30 bg-brand-lime/10 px-4 py-3"
                role="status"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-lime/15 text-brand-lime">
                  <Lock className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-brand-lime">Details locked</p>
                  <p className="mt-0.5 text-sm text-white/70">
                    Customer, address, system size, equipment and the cut-out photo are fixed. Send
                    the application below when you are ready.
                  </p>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                className="btn-cta mt-4"
                disabled={!attested || !photoSaved || confirmMutation.isPending}
                onClick={() => confirmMutation.mutate()}
              >
                {confirmMutation.isPending ? "Locking…" : "Lock these details"}
              </Button>
            )}
          </section>
        )}

        {payload && (
          <section className="rounded-xl border border-white/8 bg-[#0d1b2e] p-6">
            <h2 className="font-display text-lg font-semibold">
              {finished ? "Done" : "4. Send the application"}
            </h2>

            {finished ? (
              <SuccessPanel
                mode={submitMutation.data?.mode ?? (handedOff ? "option_b" : "option_a")}
                applicationId={
                  submitMutation.data?.submission.enaApplicationId ?? submission?.enaApplicationId ?? null
                }
                onDownload={submitMutation.data?.mode === "option_b" || handedOff ? downloadExport : undefined}
                onBack={() => navigate("/installer")}
              />
            ) : (
              <>
                <p className="mt-1 text-sm text-white/55">
                  {locked
                    ? "This sends the locked application. If your company is set to hand-off, you will get a file to submit yourself instead."
                    : "Lock the details above before sending."}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    className="btn-cta"
                    disabled={!locked || submitMutation.isPending}
                    onClick={() => submitMutation.mutate()}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {submitMutation.isPending ? "Sending…" : "Send application"}
                  </Button>
                  <Button type="button" variant="ghost" className="text-white/70 hover:text-white" onClick={() => navigate("/installer")}>
                    Back to jobs
                  </Button>
                </div>
              </>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

function Detail({
  label,
  value,
  warning = false,
  className = "",
}: {
  label: string;
  value: string;
  warning?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs uppercase tracking-wider text-white/40">{label}</dt>
      <dd className={`mt-1 text-sm ${warning ? "text-amber-200" : "text-white"}`}>{value}</dd>
    </div>
  );
}

function SuccessPanel({
  mode,
  applicationId,
  onDownload,
  onBack,
}: {
  mode: "option_a" | "option_b";
  applicationId: string | null;
  onDownload?: () => void;
  onBack: () => void;
}) {
  return (
    <div className="mt-4 space-y-4">
      <p className="inline-flex items-center gap-2 text-brand-lime">
        <CheckCircle2 className="h-5 w-5" />
        {mode === "option_b" ? "Your application file is ready." : "Application sent."}
      </p>
      {mode === "option_a" ? (
        <p className="text-sm text-white/70">
          The network has the application
          {applicationId ? ` (reference ${applicationId})` : ""}. Status updates will appear on this job.
        </p>
      ) : (
        <p className="text-sm text-white/70">
          Powlo did not send this one for you. Download the file and submit it in your own ENA account.
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        {onDownload && (
          <Button type="button" className="btn-cta" onClick={onDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download application file
          </Button>
        )}
        <Button type="button" variant="ghost" className="text-white/70 hover:text-white" onClick={onBack}>
          Back to jobs
        </Button>
      </div>
    </div>
  );
}
